import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { articlesAPI } from "../services/api";
import Breadcrumbs from "../components/Breadcrumbs";
import "./CreateContentForm.css";
import { useToasts } from "../hooks/useToasts";
import { useFolders } from "../context/FoldersContext";
import Button from "../components/Button";
import Form from "../components/Form";
import { Download } from "lucide-react";
import { CONTENT_LIMITS } from "../constants/contentLimits";

const CreateContentForm = () => {
  const navigate = useNavigate();
  const contentType = "note";
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    chapter: "",
    body: "",
  });
  const [folders, setFolders] = useState([]);
  const [selectedFolderPath, setSelectedFolderPath] = useState("");
  const [lessons, setLessons] = useState([]);
  const [allLessons, setAllLessons] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLessonDropdown, setShowLessonDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const lessonDropdownRef = useRef(null);
  const toastShownRef = useRef(false);
  const { addToast } = useToasts();
  const { invalidateCache } = useFolders();

  const buildBreadcrumbs = () => {
    return [
      { label: 'MySpace', path: '/myspace' },
      { label: 'Notes', path: '/all-notes' },
      { label: 'Create Note', path: null }
    ];
  };

  useEffect(() => {
    const loadFolders = async () => {
      try {
        const allArticles = await articlesAPI.getMyArticles();
        const lessonArticles = allArticles.data.articles.filter(
          a => a.contentType === 'lesson' && 
               a.body !== 'Folder placeholder' && 
               a.body !== 'Subfolder placeholder'
        );

        const lessonFolderPaths = Array.from(
          new Set(lessonArticles.map((article) => article.title).filter(Boolean)),
        );
        const leafFolders = lessonFolderPaths
          .filter(
            (folderPath) =>
              !lessonFolderPaths.some((candidatePath) =>
                candidatePath.startsWith(`${folderPath} / `),
              ),
          )
          .sort((a, b) => a.localeCompare(b))
          .map((folderPath) => ({
            name: folderPath.includes(" / ")
              ? folderPath.split(" / ").pop()
              : folderPath,
            fullPath: folderPath,
          }));

        setFolders(leafFolders);
        setAllLessons(lessonArticles);

        if (leafFolders.length === 0 && !toastShownRef.current) {
          addToast("No folders found. Create a lesson first.", "error-warning");
          toastShownRef.current = true;
        }
        
        if (lessonArticles.length === 0 && !toastShownRef.current) {
          addToast("No lessons found. Create a lesson first.", "error-warning");
          toastShownRef.current = true;
        }
      } catch (err) {
        console.error("Failed to load folders", err);
      }
    };
    loadFolders();
  }, [addToast]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        navigate("/create-content");
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (lessonDropdownRef.current && !lessonDropdownRef.current.contains(event.target)) {
        setShowLessonDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "title") {
      setSelectedFolderPath("");
    }
    
    if (name === 'chapter' && lessons.length > 0) {
      return;
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTitleSelect = (folder) => {
    setSelectedFolderPath(folder.fullPath);
    setFormData((prev) => ({ ...prev, title: folder.name }));
    setShowDropdown(false);
    
    const folderLessons = allLessons.filter((lesson) => lesson.title === folder.fullPath);
    setLessons(folderLessons);
    
    if (folderLessons.length === 1) {
      setFormData((prev) => ({
        ...prev,
        title: folder.name,
        chapter: folderLessons[0].chapter,
        author: folderLessons[0].author || ''
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        title: folder.name,
        chapter: '',
        author: ''
      }));
    }
  };

  const handleLessonSelect = (lesson) => {
    setFormData((prev) => ({
      ...prev,
      chapter: lesson.chapter,
      author: lesson.author || ''
    }));
    setShowLessonDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      addToast("Please select a folder from the dropdown", "error-warning");
      return;
    }
    
    const normalizeString = (str) => str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const normalizedInput = normalizeString(formData.title.trim());
    const existingFolder = selectedFolderPath
      ? folders.find((folder) => folder.fullPath === selectedFolderPath)
      : folders.find((folder) => normalizeString(folder.name) === normalizedInput);
    
    if (!existingFolder) {
      addToast("Please select an existing folder from the dropdown", "error-warning");
      return;
    }

    const finalTitle = existingFolder.fullPath;

    try {
      const response = await articlesAPI.getArticlesByFolder(finalTitle);
      let lesson = response.data.articles.find(
        a => a.contentType === 'lesson' && 
             a.chapter === formData.chapter && 
             a.body !== 'Folder placeholder' &&
             a.body !== 'Subfolder placeholder'
      );
      
      if (!lesson) {
        const lessonResponse = await articlesAPI.createArticle({
          title: finalTitle,
          author: formData.author,
          chapter: formData.chapter,
          body: formData.body,
          contentType: "lesson",
        });
        lesson = lessonResponse.data;
      }
      
      await articlesAPI.createArticle({
        title: finalTitle,
        author: lesson.author,
        chapter: lesson.chapter,
        body: formData.body,
        contentType: contentType,
        lessonId: lesson._id,
      });
      
      setFormData({ title: "", author: "", chapter: "", body: "" });
      setLessons([]);
      invalidateCache();
      addToast(`Note saved`, "success-info");
    } catch (err) {
      const errorMsg =
        err?.response?.data?.errors?.[0]?.msg ||
        err?.response?.data?.message ||
        "Failed to create note. Please try again.";
      addToast(errorMsg, "error-warning");
    }
  };

  return (
    <div className="form-content-frame form-content-frame-fullscreen">
      <Breadcrumbs path={buildBreadcrumbs()} />
      <button
        onClick={() => navigate("/create-content")}
        className="createcontent-close-btn"
        title="Back to Create Content"
      >
        <X size={32} />
      </button>
      <div className="createcontent-spacer-lg" />
      <div className="createcontent-form-layout">
        <Form
          onSubmit={handleSubmit}
          showCancel={false}
          submitText="Save"
          extraButtons={
            <Button
              className="button-btn button-btn-red"
              onClick={() => navigate("/create-content")}
              type="button"
            >
              Cancel
            </Button>
          }
          containerClassName="form-createcontent-double"
        >
          <div className="createcontent-form-row">
            <div className="createcontent-form-group" ref={dropdownRef}>
              <label htmlFor="title">Folder Title* (select existing folder)</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={() => {}}
                onFocus={() => setShowDropdown(true)}
                placeholder="Select a folder"
                maxLength="80"
                required
                autoComplete="off"
                readOnly
              />
              {showDropdown && folders.length > 0 && (
                <div className="createcontent-dropdown">
                  {folders.map((folder) => (
                    <div
                      key={folder.fullPath}
                      onClick={() => handleTitleSelect(folder)}
                      className="createcontent-dropdown-item"
                    >
                      {folder.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="createcontent-form-group" ref={lessonDropdownRef}>
              <label htmlFor="chapter">Lesson Title*</label>
              <input
                type="text"
                id="chapter"
                name="chapter"
                value={formData.chapter}
                onChange={handleChange}
                onFocus={() => lessons.length > 1 && setShowLessonDropdown(true)}
                placeholder="Required"
                maxLength="50"
                required
                autoComplete="off"
                readOnly={lessons.length > 0}
              />
              {showLessonDropdown && lessons.length > 1 && (
                <div className="createcontent-dropdown">
                  {lessons.map((lesson) => (
                    <div
                      key={lesson._id}
                      onClick={() => handleLessonSelect(lesson)}
                      className="createcontent-dropdown-item"
                    >
                      {lesson.chapter} {lesson.author && `- ${lesson.author}`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="createcontent-form-group">
            <label htmlFor="body">Body*</label>
            <textarea
              id="body"
              name="body"
              value={formData.body}
              onChange={handleChange}
              required
              placeholder="Required"
              rows="6"
              maxLength={CONTENT_LIMITS.noteBody}
            ></textarea>
          </div>
          <div className="createcontent-spacer-md" />
        </Form>
      </div>
    </div>
  );
};

export default CreateContentForm;
